import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Radio, ExternalLink, Globe } from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";
import { BASE_TZ, convertShowTimes, nowInTz, zoneLabel } from "@/lib/timezone";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function toMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

// Find a show currently airing. All comparisons are done in BASE_TZ (US Eastern)
// because show times in the DB are stored as base-tz wall-clock strings.
function findLiveShow(shows, baseNow) {
  const day = baseNow.day;
  const minutes = baseNow.hh * 60 + baseNow.mm;
  return shows.find((s) => {
    if (!s.start_time || !s.end_time) return false;
    const days = Array.isArray(s.days_of_week) && s.days_of_week.length > 0
      ? s.days_of_week
      : [s.day_of_week ?? 0];
    const start = toMinutes(s.start_time);
    const end = toMinutes(s.end_time);
    if (end > start) {
      return days.includes(day) && minutes >= start && minutes < end;
    }
    // crosses midnight
    const prevDay = (day + 6) % 7;
    return (
      (days.includes(day) && minutes >= start) ||
      (days.includes(prevDay) && minutes < end)
    );
  });
}

function findNextShow(shows, baseNow) {
  const day = baseNow.day;
  const minutes = baseNow.hh * 60 + baseNow.mm;
  let best = null;
  let bestDelta = Infinity;
  for (const s of shows) {
    if (!s.start_time) continue;
    const days = Array.isArray(s.days_of_week) && s.days_of_week.length > 0
      ? s.days_of_week
      : [s.day_of_week ?? 0];
    const start = toMinutes(s.start_time);
    for (const d of days) {
      let dayDelta = (d - day + 7) % 7;
      let delta = dayDelta * 24 * 60 + (start - minutes);
      if (delta <= 0) delta += 7 * 24 * 60;
      if (delta < bestDelta) { bestDelta = delta; best = s; }
    }
  }
  return best;
}

// Convert a show's canonical (base-tz) anchor to the viewer's timezone for
// display. Returns { day, start_time, end_time }.
function displayTimes(s, targetTz) {
  const days = Array.isArray(s.days_of_week) && s.days_of_week.length > 0
    ? s.days_of_week
    : [s.day_of_week ?? 0];
  const anchor = days[0];
  return convertShowTimes(anchor, s.start_time, s.end_time, targetTz);
}

export default function NowLiveBanner() {
  const { timezone, isBase } = useTimezone();
  const [shows, setShows] = useState([]);
  const [, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shows");
        setShows(data);
      } catch {}
    })();
    const id = setInterval(() => setTick((t) => t + 1), 30_000); // re-check every 30s
    return () => clearInterval(id);
  }, []);

  if (shows.length === 0) return null;

  const baseNow = nowInTz(BASE_TZ);
  const live = findLiveShow(shows, baseNow);
  const next = !live ? findNextShow(shows, baseNow) : null;

  const liveDisplay = live ? displayTimes(live, timezone) : null;
  const nextDisplay = next ? displayTimes(next, timezone) : null;

  return (
    <div className="relative z-20 border-y border-[var(--ff-border)] bg-[var(--ff-surface)]" data-testid="now-live-banner">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {live ? (
            <>
              <span className="relative flex h-3 w-3" data-testid="live-indicator-dot">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--ff-red)] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--ff-red)]"></span>
              </span>
              <div className="label-eyebrow text-[var(--ff-red)]">▮ ON AIR NOW</div>
              <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
              <div className="flex items-center gap-3">
                <Radio size={16} className="text-[var(--ff-text-dim)]" />
                <div>
                  <div className="font-display text-lg leading-none" data-testid="live-show-title">{live.title}</div>
                  <div className="font-mono-ui text-xs text-[var(--ff-text-dim)] mt-1 flex items-center gap-1.5 flex-wrap">
                    <span>{DAY_NAMES[liveDisplay.day].toUpperCase()} / {liveDisplay.start_time}–{liveDisplay.end_time}</span>
                    {!isBase && (
                      <span className="inline-flex items-center gap-1 text-[var(--ff-red)]" data-testid="live-tz-label">
                        <Globe size={10} /> {zoneLabel(timezone)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : next ? (
            <>
              <Radio size={16} className="text-[var(--ff-text-dim)]" />
              <div className="label-eyebrow">▮ UP NEXT</div>
              <div className="hidden md:block w-px h-6 bg-[var(--ff-border)]"></div>
              <div>
                <div className="font-display text-lg leading-none" data-testid="next-show-title">{next.title}</div>
                <div className="font-mono-ui text-xs text-[var(--ff-text-dim)] mt-1 flex items-center gap-1.5 flex-wrap">
                  <span>{DAY_NAMES[nextDisplay.day].toUpperCase()} / {nextDisplay.start_time}</span>
                  {!isBase && (
                    <span className="inline-flex items-center gap-1 text-[var(--ff-red)]" data-testid="next-tz-label">
                      <Globe size={10} /> {zoneLabel(timezone)}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {live && live.youtube_url && (
            <a
              href={live.youtube_url}
              target="_blank"
              rel="noreferrer"
              className="btn-primary text-xs"
              data-testid="watch-live-btn"
            >
              <ExternalLink size={12} /> Watch Live
            </a>
          )}
          <a href="/schedule" className="btn-ghost text-xs" data-testid="banner-schedule-link">
            Full Schedule →
          </a>
        </div>
      </div>
    </div>
  );
}
